/**
 * App container.
 * @module components/theme/App/App
 */

import React, { Component } from 'react';
import jwtDecode from 'jwt-decode';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { asyncConnect, Helmet } from '@plone/volto/helpers';
import { renderRoutes } from 'react-router-config';
import { Slide, ToastContainer, toast } from 'react-toastify';
import split from 'lodash/split';
import join from 'lodash/join';
import trim from 'lodash/trim';
import cx from 'classnames';
import config from '@plone/volto/registry';
import { PluggablesProvider } from '@plone/volto/components/manage/Pluggable';
import { visitBlocks } from '@plone/volto/helpers/Blocks/Blocks';
import { injectIntl } from 'react-intl';
import SlotRenderer from '@plone/volto/components/theme/SlotRenderer/SlotRenderer';

import Error from '@plone/volto/error';

import * as Sentry from '@sentry/react';

import {
  Breadcrumbs,
  Header,
  Icon,
  OutdatedBrowser,
  AppExtras,
  SkipLinks,
} from '@plone/volto/components';
import Footer from '@fhnw/components/Footer/Footer';
import GoToTopButton from '@fhnw/components/GoToTop/GoToTop.tsx';
import { navItems, logos } from '@fhnw/components/Footer/Footer.data';
import {
  BodyClass,
  getBaseUrl,
  getView,
  hasApiExpander,
  isCmsUi,
} from '@plone/volto/helpers';
import {
  getBreadcrumbs,
  getContent,
  getNavigation,
  getTypes,
  getWorkflow,
} from '@plone/volto/actions';

import clearSVG from '@plone/volto/icons/clear.svg';
import MultilingualRedirector from '@plone/volto/components/theme/MultilingualRedirector/MultilingualRedirector';
import WorkingCopyToastsFactory from '@plone/volto/components/manage/WorkingCopyToastsFactory/WorkingCopyToastsFactory';
import LockingToastsFactory from '@plone/volto/components/manage/LockingToastsFactory/LockingToastsFactory';
import LocationMap from '@fhnw/components/LocationMap/LocationMap.tsx';
import GlobalSearch from '@fhnw/components/GlobalSearch/GlobalSearch';
import { GoogleAnalyticsDataForm } from '@fhnw/components/GoogleAnalyticsDataForm/GoogleAnalyticsDataForm.tsx';
import CookieConsent from '@fhnw/components/CookieConsent/CookieConsent.tsx';
import { VocabProvider } from '@fhnw/components/VocabContext/VocabContext.tsx';
import { getLangFromPath } from '@fhnw/helpers/getLangFromPath.ts';
import Cookies from 'universal-cookie';

const cookies = new Cookies();
const token = cookies.get('auth_token');
const user = token ? jwtDecode(token).sub : 'Anonymous';

const env = (typeof window !== 'undefined' ? window : process)?.env;

const FHNW_ENV = env?.RAZZLE_FHNW_ENV || 'NO_ENV';
const FORCE_SENTRY = env?.RAZZLE_FORCE_SENTRY || 0;

if (
  FHNW_ENV === 'PROD' ||
  FHNW_ENV === 'TEST' ||
  FHNW_ENV === 'DEV' ||
  FORCE_SENTRY
) {
  Sentry.init({
    dsn: 'https://fdd4631abf6107ba9d8ff3b8dddc1be7@o4508420237492224.ingest.de.sentry.io/4509825609433168',
    sendDefaultPii: true,
    enableLogs: true,
    environment: FHNW_ENV,
  });

  Sentry.setContext('Context', {
    user,
    release_git_tag: env?.RAZZLE_RELEASE_GIT_TAG || 'NO_TAG',
    release_git_branch: env?.RAZZLE_RELEASE_GIT_BRANCH || 'NO_BRANCH',
  });
}

/**
 * @export
 * @class App
 * @extends {Component}
 */
export class App extends Component {
  /**
   * Property types.
   * @property {Object} propTypes Property types.
   * @static
   */
  static propTypes = {
    pathname: PropTypes.string.isRequired,
  };

  state = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  constructor(props) {
    super(props);
    this.mainRef = React.createRef();
  }

  /**
   * @method componentWillReceiveProps
   * @param {Object} nextProps Next properties
   * @returns {undefined}
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.pathname !== this.props.pathname) {
      if (this.state.hasError) {
        this.setState({ hasError: false });
      }
    }
  }

  /**
   * ComponentDidCatch
   * @method ComponentDidCatch
   * @param {string} error  The error
   * @param {string} info The info
   * @returns {undefined}
   */
  componentDidCatch(error, info) {
    this.setState({ hasError: true, error, errorInfo: info });
    config.settings.errorHandlers.forEach((handler) => handler(error));
  }

  dispatchContentClick = (event) => {
    if (event.target === event.currentTarget) {
      const rect = this.mainRef.current.getBoundingClientRect();
      if (event.clientY > rect.bottom) {
        document.dispatchEvent(new Event('voltoClickBelowContent'));
      }
    }
  };

  /**
   * Render method.
   * @method render
   * @returns {string} Markup for the component.
   */
  render() {
    const { views } = config;
    const path = getBaseUrl(this.props.pathname);
    const action = getView(this.props.pathname);
    const isCmsUI = isCmsUi(this.props.pathname);
    const ConnectionRefusedView = views.errorViews.ECONNREFUSED;
    const env = config?.settings?.['FHNW_ENV'];
    const isStartPage = /^\/(de|fr|en)?\/?$/.test(this.props.pathname);

    const languageFromPath = getLangFromPath(this.props.pathname);

    const language =
      this.props.content?.language?.token ??
      languageFromPath ??
      this.props.intl?.locale;

    let criticalSlickCss = '';
    let criticalSlickMinCss = '';

    if (__SERVER__ && isStartPage) {
      const fs = require('fs');
      const fsPath = require('path');

      const slickCssPath = fsPath.resolve(
        __dirname,
        '../../../packages/volto/public/static/css/fhnw.slick.css',
      );

      criticalSlickCss = fs.readFileSync(slickCssPath, 'utf8');

      const slickMinCssPath = fsPath.resolve(
        __dirname,
        '../../../packages/volto/public/static/css/slick.min.css',
      );

      criticalSlickMinCss = fs.readFileSync(slickMinCssPath, 'utf8');
    }

    return (
      <VocabProvider>
        <PluggablesProvider>
          {(env === 'PROD' || env === 'TEST') && <CookieConsent />}
          {language && isStartPage && (
            <Helmet>
              <html lang={language} />
              {__SERVER__ && <style type="text/css">{criticalSlickCss}</style>}
              {__SERVER__ && (
                <style type="text/css">{criticalSlickMinCss}</style>
              )}
              <link rel="preload" as="style" href="/static/css/slick.min.css" />
              <link rel="stylesheet" href="/static/css/slick.min.css" />
              <link
                rel="preload"
                as="style"
                href="/static/css/slick-theme.min.css"
              />
              <link rel="stylesheet" href="/static/css/slick-theme.min.css" />
              <link
                rel="preload"
                as="style"
                href="/static/css/fhnw.slick.css"
              />
              <link rel="stylesheet" href="/static/css/fhnw.slick.css" />
              <link
                rel="preconnect"
                href="https://fonts.gstatic.com"
                crossOrigin="anonymous"
              />
            </Helmet>
          )}
          {language && !isStartPage && (
            <Helmet>
              <html lang={language} />
              <link
                rel="preconnect"
                href="https://fonts.gstatic.com"
                crossOrigin="anonymous"
              />
            </Helmet>
          )}
          <BodyClass className={`view-${action}view`} />
          {/* Body class depending on content type */}
          {this.props.content && this.props.content['@type'] && (
            <BodyClass
              className={cx(
                `contenttype-${this.props.content['@type']
                  .replace(' ', '-')
                  .toLowerCase()}`,
                `env-${env}`,
              )}
            />
          )}
          {/* Body class depending on sections */}
          <BodyClass
            className={cx({
              [trim(join(split(this.props.pathname, '/'), ' section-'))]:
                this.props.pathname !== '/',
              siteroot: this.props.pathname === '/',
              'is-authenticated': !!this.props.token,
              'is-anonymous': !this.props.token,
              'cms-ui': isCmsUI,
              'public-ui': !isCmsUI,
            })}
          />
          <MultilingualRedirector
            pathname={this.props.pathname}
            contentLanguage={language}
          >
            <SkipLinks />
            <main
              id="content-area"
              ref={this.mainRef}
              className="content-area"
              role="main"
              tabIndex="-1"
            >
              <div id="navigation" tabIndex="-1">
                <Header pathname={path} />
              </div>
              <div
                className="content-slot"
                onClick={this.dispatchContentClick}
                role="presentation"
              >
                <SlotRenderer
                  name="startSliderSlot"
                  content={this.props.content || {}}
                  pathname={this.props.pathname}
                />
                <div className="hero-slot">
                  <SlotRenderer
                    name="heroSlot"
                    content={this.props.content || {}}
                  />
                  <GlobalSearch />
                </div>
                <Breadcrumbs pathname={path} />
                <OutdatedBrowser />
                {this.props.connectionRefused ? (
                  <ConnectionRefusedView />
                ) : this.state.hasError ? (
                  <Error
                    message={this.state.error.message}
                    stackTrace={this.state.errorInfo.componentStack}
                  />
                ) : (
                  renderRoutes(this.props.route.routes, {
                    staticContext: this.props.staticContext,
                  })
                )}
                <LocationMap content={this.props.content} />
                <Footer navItems={navItems} logos={logos} />
                <GoToTopButton />
                <GoogleAnalyticsDataForm content={this.props.content} />
              </div>
            </main>
          </MultilingualRedirector>
          <LockingToastsFactory
            content={this.props.content}
            user={this.props.userId}
          />
          <WorkingCopyToastsFactory content={this.props.content} />
          <ToastContainer
            position={toast.POSITION.BOTTOM_CENTER}
            hideProgressBar
            transition={Slide}
            autoClose={5000}
            closeButton={
              <Icon
                className="toast-dismiss-action"
                name={clearSVG}
                size="18px"
              />
            }
          />
          <AppExtras {...this.props} />
        </PluggablesProvider>
      </VocabProvider>
    );
  }
}

export const __test__ = connect(
  (state, props) => ({
    pathname: props.location.pathname,
    token: state.userSession.token,
    content: state.content.data,
    apiError: state.apierror.error,
    connectionRefused: state.apierror.connectionRefused,
  }),
  {},
)(App);

export const fetchContent = async ({ store, location }) => {
  const content = await store.dispatch(
    getContent(getBaseUrl(location.pathname)),
  );

  const promises = [];
  const { blocksConfig } = config.blocks;

  const visitor = ([id, data]) => {
    const blockType = data['@type'];
    const { getAsyncData } = blocksConfig[blockType];
    if (getAsyncData) {
      const p = getAsyncData({
        store,
        dispatch: store.dispatch,
        path: location.pathname,
        location,
        id,
        data,
        blocksConfig,
        content,
      });
      if (!p?.length) {
        throw new Error(
          'You should return a list of promises from getAsyncData',
        );
      }
      promises.push(...p);
    }
  };

  visitBlocks(content, visitor);

  await Promise.allSettled(promises);

  return content;
};

export function connectAppComponent(AppComponent) {
  return compose(
    asyncConnect([
      {
        key: 'breadcrumbs',
        promise: ({ location, store: { dispatch } }) => {
          // Do not trigger the breadcrumbs action if the expander is present
          if (
            __SERVER__ &&
            !hasApiExpander('breadcrumbs', getBaseUrl(location.pathname))
          ) {
            return dispatch(getBreadcrumbs(getBaseUrl(location.pathname)));
          }
        },
      },
      {
        key: 'content',
        promise: ({ location, store }) =>
          __SERVER__ && fetchContent({ store, location }),
      },
      {
        key: 'navigation',
        promise: ({ location, store: { dispatch } }) => {
          // Do not trigger the navigation action if the expander is present
          if (
            __SERVER__ &&
            !hasApiExpander('navigation', getBaseUrl(location.pathname))
          ) {
            return dispatch(
              getNavigation(
                getBaseUrl(location.pathname),
                config.settings.navDepth,
              ),
            );
          }
        },
      },
      {
        key: 'types',
        promise: ({ location, store: { dispatch } }) => {
          // Do not trigger the types action if the expander is present
          if (
            __SERVER__ &&
            !hasApiExpander('types', getBaseUrl(location.pathname))
          ) {
            return dispatch(getTypes(getBaseUrl(location.pathname)));
          }
        },
      },
      {
        key: 'workflow',
        promise: ({ location, store: { dispatch } }) =>
          __SERVER__ && dispatch(getWorkflow(getBaseUrl(location.pathname))),
      },
    ]),
    injectIntl,
    connect(
      (state, props) => ({
        pathname: props.location.pathname,
        token: state.userSession.token,
        userId: state.userSession.token
          ? jwtDecode(state.userSession.token).sub
          : '',
        content: state.content.data,
        apiError: state.apierror.error,
        connectionRefused: state.apierror.connectionRefused,
      }),
      null,
    ),
  )(AppComponent);
}

export default connectAppComponent(App);
